import type { ReactNode } from 'react';
import { useClientStanding } from '../api/standing.ts';
import { LoadFailure } from './LoadFailure.tsx';
import { formatDate } from '../dates.ts';
import type {
  AlliedRole, CaseContact, HeldPower, StandingAppointment, StandingVisits, TimelineEntry,
} from '../api/types.ts';

const CATEGORIES: { key: HeldPower['category']; label: string }[] = [
  { key: 'PERSONAL_NEEDS', label: 'Personal needs' },
  { key: 'PROPERTY_MANAGEMENT', label: 'Property' },
];

/**
 * What is true about this client now, above the history of how it came to be.
 *
 * Where they live and for how long, the authority the agency holds today, the
 * appointment it holds it under, and the people on the legal side of the case.
 * History — placements, stays, visits — is below it and not interleaved: a
 * case manager opening a record needs the current answer first, and a
 * timeline makes them reconstruct it.
 *
 * Authority is shown per side, and a side the agency does not hold is said in
 * words. A co-guardianship where another guardian holds the property alone
 * must not read as if the agency could sell the house.
 */
export function CurrentStanding({
  clientId,
  legalStatus,
  entries,
}: {
  clientId: string;
  legalStatus: string | null;
  /** The timeline, once loaded. Where they live comes from its current placement. */
  entries: TimelineEntry[] | undefined;
}) {
  const standing = useClientStanding(clientId);

  return (
    <section className="bg-white lg:rounded-lg lg:shadow">
      <h2 className="px-4 pt-3 pb-1 text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
        Now
      </h2>

      <LivesAt entries={entries} />

      {standing.error && !standing.data ? (
        <LoadFailure
          what="This client's authority and contacts"
          error={standing.error}
          onRetry={() => void standing.refetch()}
          retrying={standing.isFetching}
          forbiddenIsSession={false}
        />
      ) : !standing.data ? (
        <p className="px-4 py-4 text-neutral-500">Loading authority and contacts…</p>
      ) : (
        <>
          <VisitsThisYear visits={standing.data.visits} />
          <Authority
            appointments={standing.data.appointments}
            powers={standing.data.authority}
            legalStatus={legalStatus}
          />
          <People contacts={standing.data.contacts} />
        </>
      )}
    </section>
  );
}

/**
 * This year's visits so far. The visits dashboard shows pace and priority, not
 * the counts, so the counts live on the client's own page. Nothing is shown for
 * a client who owes no visits by legal status.
 */
function VisitsThisYear({ visits }: { visits: StandingVisits | null }) {
  if (!visits) return null;
  return (
    <Block title="Visits this year">
      <p className="text-[0.9375rem] text-neutral-800 tabular-nums">
        {visits.visits_done} {visits.visits_done === 1 ? 'visit' : 'visits'} so far in {visits.visit_year}
        {visits.visits_needed > 0 ? `, of ${visits.visits_needed} owed for the year` : ', none owed'}
      </p>
      {visits.last_visit_date && (
        <p className="text-sm text-neutral-500">Last visit {formatDate(visits.last_visit_date)}</p>
      )}
    </Block>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-neutral-200 px-4 py-3">
      <h3 className="text-sm font-bold text-neutral-800">{title}</h3>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** "3 years, 2 months" from a day count PostgreSQL computed. */
function howLong(days: number): string {
  if (days < 31) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(days / 365.25);
  const rest = Math.floor((days - years * 365.25) / 30.44);
  return `${years} year${years === 1 ? '' : 's'}${rest > 0 ? `, ${rest} month${rest === 1 ? '' : 's'}` : ''}`;
}

function LivesAt({ entries }: { entries: TimelineEntry[] | undefined }) {
  // Nothing until the timeline has answered: the header already says where to
  // find them, and "not recorded" before an answer would be a false statement.
  if (!entries) return null;
  const placement = entries.find((e) => e.entry_kind === 'Placement' && e.is_current);
  const stay = entries.find((e) => e.entry_kind === 'Stay' && e.is_current);

  return (
    <Block title="Lives at">
      {placement ? (
        <p className="text-[0.9375rem] text-neutral-800">
          <span className="font-medium">{placement.location ?? placement.kind_label ?? 'A placement'}</span>
          {placement.city && <span className="text-neutral-600">, {placement.city}</span>}
          {placement.started_on && (
            <span className="block text-sm text-neutral-600">
              Since {formatDate(placement.started_on)}
              {placement.days !== null && ` · ${howLong(placement.days)}`}
              {placement.kind_label && ` · ${placement.kind_label}`}
            </span>
          )}
        </p>
      ) : (
        <p className="text-[0.9375rem] text-neutral-600">No current placement recorded.</p>
      )}
      {stay && (
        <p className="mt-1.5 text-[0.9375rem] text-neutral-800">
          <span className="font-medium">Away: {stay.location ?? stay.kind_label ?? 'a temporary stay'}</span>
          <span className="block text-sm text-neutral-600">
            Since {stay.started_on ? formatDate(stay.started_on) : 'an unrecorded date'}
            {stay.bed_held ? ' · placement held' : ''}
            {stay.expected_return_on && ` · expected back ${formatDate(stay.expected_return_on)}`}
          </span>
        </p>
      )}
    </Block>
  );
}

function roleWords(role: AlliedRole): string {
  return role === 'SOLE' ? 'Sole guardian' : role === 'CO' ? 'Co-guardian' : 'Not held by the agency';
}

function Authority({
  appointments,
  powers,
  legalStatus,
}: {
  appointments: StandingAppointment[];
  powers: HeldPower[];
  legalStatus: string | null;
}) {
  /**
   * Not appointed means nothing is held, whatever the appointment row says.
   *
   * Migration 024 carried an appointment across for every client from the
   * client record, Pipeline clients included, with the scope and commission
   * date the record held — so a client pending appointment has a row naming the
   * agency sole guardian of both sides and a commission date in the future.
   * Shown as it stands, that is a statement of authority the agency does not
   * have. Until the row is corrected, the page says what is true.
   */
  if (legalStatus === 'Pipeline') {
    const index = appointments.find((a) => a.index_number)?.index_number;
    return (
      <Block title="Authority">
        <p className="text-[0.9375rem] text-neutral-800">
          Pending appointment. The agency holds no authority until the court appoints it.
        </p>
        {index && (
          <p className="mt-0.5 text-sm text-neutral-500">
            Index number <span className="font-mono">{index}</span>
          </p>
        )}
      </Block>
    );
  }

  const live = appointments.filter((a) => a.is_live);
  const ended = appointments.length - live.length;

  if (appointments.length === 0) {
    return (
      <Block title="Authority">
        <p className="text-[0.9375rem] text-neutral-600">
          No appointment on file, so no authority is recorded.
        </p>
      </Block>
    );
  }

  return (
    <Block title="Authority">
      {live.length === 0 && (
        <p className="text-[0.9375rem] text-neutral-600">
          No live appointment. {ended} ended appointment{ended === 1 ? '' : 's'} on file.
        </p>
      )}

      {live.map((a) => (
        <div key={a.id} className="mb-3 last:mb-0">
          <p className="text-[0.9375rem] text-neutral-800">
            <span className="font-mono">{a.index_number ?? 'No index number'}</span>
            <span className="text-neutral-600">
              {' · '}
              {[
                a.is_temporary ? 'Temporary' : 'Permanent',
                a.is_successor && 'Successor',
                a.is_limited && 'Limited',
                a.is_trustee && 'Trustee',
              ].filter(Boolean).join(', ')}
              {a.commission_date ? ` · commissioned ${formatDate(a.commission_date)}` : ' · not yet commissioned'}
            </span>
          </p>
          {a.prior_index_numbers && a.prior_index_numbers.length > 0 && (
            <p className="text-sm text-neutral-500">
              Took over from {a.prior_index_numbers.join(', ')}
            </p>
          )}

          <dl className="mt-2 space-y-2">
            {CATEGORIES.map((c) => {
              const role = c.key === 'PERSONAL_NEEDS' ? a.allied_person_role : a.allied_property_role;
              // By appointment, never by index number: the same number is written
              // several ways, and a successor's powers come from another proceeding.
              //
              // One line per power. v_current_authority has a row per order that
              // grants it, so a power granted by the temporary order and again by
              // the permanent one arrives twice. It is one authority held: its
              // qualifiers and the orders it was granted by reference to are merged.
              const held = [
                ...powers
                  .filter((p) => p.category === c.key && p.appointment_id === a.id)
                  .reduce((byCode, p) => {
                    const line = byCode.get(p.power_code) ?? {
                      code: p.power_code, label: p.power_label,
                      qualifiers: new Set<string>(), references: new Set<string>(),
                    };
                    if (p.qualifier) line.qualifiers.add(p.qualifier);
                    for (const r of p.granted_by_reference_to ?? []) line.references.add(r.signed_date);
                    return byCode.set(p.power_code, line);
                  }, new Map<string, { code: string; label: string; qualifiers: Set<string>; references: Set<string> }>())
                  .values(),
              ];
              return (
                <div key={c.key}>
                  <dt className="text-sm text-neutral-500">
                    {c.label} ·{' '}
                    <span className={role ? 'font-semibold text-neutral-800' : 'font-semibold text-neutral-600'}>
                      {roleWords(role)}
                    </span>
                  </dt>
                  <dd>
                    {role === null ? null : held.length === 0 ? (
                      <p className="text-sm text-neutral-600">No powers recorded on the orders on file.</p>
                    ) : (
                      <ul className="mt-0.5 space-y-0.5">
                        {held.map((p) => (
                          <li key={p.code} className="text-[0.9375rem] text-neutral-800">
                            {p.label}
                            {p.qualifiers.size > 0 && (
                              <span className="text-neutral-600"> — {[...p.qualifiers].join('; ')}</span>
                            )}
                            {p.references.size > 0 && (
                              <span className="block text-xs text-neutral-500">
                                by reference to the order of{' '}
                                {[...p.references].sort().map((d) => formatDate(d)).join(', ')}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </Block>
  );
}

function People({ contacts }: { contacts: CaseContact[] }) {
  return (
    <Block title="On the case">
      {contacts.length === 0 ? (
        <p className="text-[0.9375rem] text-neutral-600">
          No co-guardian, evaluator, examiner or counsel recorded.
        </p>
      ) : (
        <ul className="space-y-2">
          {contacts.map((c) => (
            <li key={c.assignment_id} className="text-[0.9375rem] text-neutral-800">
              <span className="text-sm text-neutral-500">{c.role_label}</span>
              <span className="block font-medium">
                {c.name ?? 'Name not recorded'}
                {c.credentials && <span className="font-normal text-neutral-600">, {c.credentials}</span>}
              </span>
              {c.authority_note && <span className="block text-sm text-neutral-600">{c.authority_note}</span>}
              {c.do_not_contact && (
                <span className="block text-sm font-semibold text-neutral-900">
                  Do not contact{c.do_not_contact_note ? `: ${c.do_not_contact_note}` : ''}
                </span>
              )}
              {!c.do_not_contact && (c.phone || c.email) && (
                <span className="block text-sm text-neutral-600">
                  {[c.phone, c.email].filter(Boolean).join(' · ')}
                </span>
              )}
              {(c.appointed_date ?? c.started_on) && (
                <span className="block text-xs text-neutral-500">
                  Since {formatDate((c.appointed_date ?? c.started_on)!)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}
